import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";

export default function ResetPassword() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const inputCls = "bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 rounded-[12px] h-11 focus-visible:ring-[#007BFF]";

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error(t("reset_short")); return; }
    if (pw !== pw2) { toast.error(t("reset_mismatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
      toast.success(t("reset_success"));
    } catch {
      toast.error(t("reset_invalid_link"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] px-5" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-[#111111] border border-white/10 rounded-[16px] p-8">
        <div className="font-display text-2xl font-extrabold mb-6 text-white">
          Gaim<span className="text-[#007BFF] text-glow-blue">Time</span>
        </div>
        {done ? (
          <div className="text-center space-y-5" data-testid="reset-done">
            <CheckCircle2 className="mx-auto text-[#00E676]" size={48} />
            <p className="text-white/70">{t("reset_success")}</p>
            <Button data-testid="reset-go-login" onClick={() => navigate("/")}
              className="w-full bg-[#007BFF] hover:bg-[#0056b3] rounded-[12px] h-12 font-semibold">
              {t("sign_in")}
            </Button>
          </div>
        ) : !token ? (
          <p className="text-white/70 text-center" data-testid="reset-no-token">{t("reset_invalid_link")}</p>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <h1 className="font-display text-xl font-bold text-white">{t("reset_title")}</h1>
            <p className="text-xs text-white/40 -mt-1">{t("reset_desc")}</p>
            <div className="relative">
              <Input data-testid="reset-new-password" type={showPw ? "text" : "password"} className={`${inputCls} pr-10`} placeholder={t("reset_new_pw")} value={pw} onChange={(e) => setPw(e.target.value)} required />
              <button type="button" data-testid="toggle-reset-password" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors">
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <Input data-testid="reset-confirm-password" type={showPw ? "text" : "password"} className={inputCls} placeholder={t("reset_confirm_pw")} value={pw2} onChange={(e) => setPw2(e.target.value)} required />
            <Button data-testid="reset-submit" type="submit" disabled={loading}
              className="w-full bg-[#007BFF] hover:bg-[#0056b3] rounded-[12px] h-12 font-semibold text-base transition-all hover:scale-[1.02]">
              {loading ? <Loader2 className="animate-spin" /> : t("reset_submit")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
